import { Injectable } from '@angular/core'
import { BarterResultDataInterface, BarterResultInterface } from './barterResult.interface'
import { ICalculateBarterResultParcelsDate } from '../interfaces/calculate-barter-result-data.interface'
import { filter } from 'lodash'
import { LpisKultura } from '../../../api/backend'

@Injectable({
    providedIn: 'root',
})
export class BarterResultService {

    /**
     * Calculate results table from barter data and list of lpis kulturas.
     * Total subproperty is also calculated.
     *
     * WARNING: do not mutate barter parameter
     *
     * @param lpisKulturas
     * @param concernedParcelIds
     * @param concernedParcelAcreages
     * @param counterpartyParcelIds
     * @param counterpartyParcelAcreages
     * @param passedParcelIds
     * @param passedParcelAcreages
     * @param receivedParcelIds
     * @param receivedParcelAcreages
     */
    calculateBarterResult(data: ICalculateBarterResultParcelsDate, lpisKulturas: LpisKultura[]): BarterResultInterface {
        let result: BarterResultInterface = {
            after: {
                results: {},
                total: {
                    concernedUsedAcreage: 0,
                    counterpartyUsedAcreage: 0,
                    diffConcerned: 0,
                    diffCounterparty: 0,
                },
            },
            before: {
                results: {},
                total: {
                    concernedUsedAcreage: 0,
                    counterpartyUsedAcreage: 0,
                    diffConcerned: 0,
                    diffCounterparty: 0,
                },
            },
        }
        lpisKulturas.forEach(lk => {
            result.after.results[lk.id] = {
                lpisKultura: lk,
                concernedUsedAcreage: 0,
                counterpartyUsedAcreage: 0,
            }
            result.before.results[lk.id] = {
                lpisKultura: lk,
                concernedUsedAcreage: 0,
                counterpartyUsedAcreage: 0,
            }
        })

        data.stateConcernedParcelAcreages
            .filter(acreage => data.stateConcernedParcelIds.includes(acreage.parcelId))
            .forEach(acreage => {
                this.addUsedAcreageToResult(result.before, true, acreage)
                this.addUsedAcreageToResult(result.after, true, acreage)
            })
        data.statePassedParcelAcreages
            .filter(acreage => data.statePassedParcelIds.includes(acreage.parcelId))
            .forEach(acreage => {
                this.addUsedAcreageToResult(result.after, true, acreage)
            })
        data.stateCounterpartyParcelAcreages
            .filter(acreage => data.stateCounterpartyParcelIds.includes(acreage.parcelId))
            .forEach(acreage => {
                this.addUsedAcreageToResult(result.before, false, acreage)
                this.addUsedAcreageToResult(result.after, false, acreage)
            })
        data.stateReceivedParcelAcreages
            .filter(acreage => data.stateReceivedParcelIds.includes(acreage.parcelId))
            .forEach(acreage => {
                this.addUsedAcreageToResult(result.after, false, acreage)
            })

        this.updateSideValuesOfRow(result.before.total)
        this.updateSideValuesOfRow(result.after.total)

        return result
    }

    /**
     * Remove zero diff values from list of results
     * Alternative solution for ng-repeat filter because filter cannot be used on object.
     * @param items
     * @returns {{}}
     */
    filterOutZeroDiff(items): any {
        return filter(items, value => value.diffCounterparty || value.diffConcerned)
    }

    /**
     * Helper method to add used acreage value to proper side of barter results.
     * @param result
     * @param isConcerned True=concerned list, False=counterparty list
     * @param barterParcel
     */
    private addUsedAcreageToResult(result: BarterResultDataInterface, isConcerned: boolean, barterParcel): void {
        if (barterParcel ) {
            let res = result.results[barterParcel.lpisKultura.id]
            if (!res) {
                return
            }
            const partyKey = isConcerned ? 'concernedUsedAcreage' : 'counterpartyUsedAcreage'
            res[partyKey] += parseInt(barterParcel.acreage)
            result.total[partyKey] += parseInt(barterParcel.acreage)
            res.diffConcerned = 0
            res.diffCounterparty = 0
            this.updateSideValuesOfRow(res)
        }
    }

    private updateSideValuesOfRow(row): void {
        if (row.concernedUsedAcreage >= row.counterpartyUsedAcreage) {
            row.diffConcerned = Math.abs(row.concernedUsedAcreage - row.counterpartyUsedAcreage)
        } else {
            row.diffCounterparty = Math.abs(row.concernedUsedAcreage - row.counterpartyUsedAcreage)
        }
    }
}
